/**
 * @file    useCprTrendSync.ts
 * @scope   ENTERPRISE
 * @summary Routes the drawer trend control (`selectedChartTrend`) into the CPR slice (`SET_TREND`).
 * @consumes CprContext, active descriptor
 * @produces useCprTrendSync
 * @sideEffects Dispatches `SET_TREND` while CPR is the active subtype.
 * @notes   CPR persists the trend at widget level from its slice, not from the drawer trend option.
 *
 * TODO(ai): assumes the Guardrails trend option already carries the backend `TimePeriodType` name
 * (`Monthly`, `Weekly`…). If Guardrails sends numeric enums for CPR, map them here before dispatch.
 */
import { useEffect } from 'react';
import { TrendOptions } from 'core-ui/SelectTrendInput/SelectTrendInput';
import { useCprContext } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/state/CprContext';
import { CprTrend } from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/model';
import {
  CPR_TREND_PERIOD_TYPES,
  toCprEnum,
} from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/constants';
import { ActiveEngineDescriptor } from './useActiveEngineDescriptor';
import { isCprDescriptor } from './useActiveEngineSlice';

/** @returns The CPR trend for a drawer trend option, `undefined` when none is selected. */
export const toCprTrend = (option?: TrendOptions | null): CprTrend | undefined => {
  const trend = option?.trend;
  if (!trend?.timePeriodType) return undefined;
  // WHY: GetData sends `monthly`; the contract expects the enum name `Monthly`.
  const timePeriodType =
    toCprEnum(trend.timePeriodType, CPR_TREND_PERIOD_TYPES) ?? String(trend.timePeriodType);
  return {
    displayName: trend.displayName,
    timePeriodType,
    selectedValue: trend.selectedValue,
    intervalMinValue: trend.intervalMinValue,
    intervalMaxValue: trend.intervalMaxValue,
  };
};

export const useCprTrendSync = (
  descriptor: ActiveEngineDescriptor,
  selectedChartTrend?: TrendOptions | null
): void => {
  const { dispatch } = useCprContext();
  const isCpr = isCprDescriptor(descriptor);

  useEffect(() => {
    if (!isCpr) return;
    dispatch({ type: 'SET_TREND', trend: toCprTrend(selectedChartTrend) });
  }, [isCpr, selectedChartTrend, dispatch]);
};

export default useCprTrendSync;
